import axios from 'axios';
import { API_BASE_URL, API_TOKEN, STORAGE_KEYS } from '../config';
import { safeGet, safeSet, safeRemove } from './storage';
import { assessmentMetadata, normalizeAssessmentLanguage, uiLanguage } from './assessmentLanguage';

const bankPrefix = 'adultQuestionBank_';
export const bankCacheKey = (context) => `${bankPrefix}${normalizeAssessmentLanguage(context.assessment_language || context.defaultLang)}_${context.question_bank_version}_${context.question_bank_checksum}`;
export const bankAnswerCacheKey = (context) => `adultAnswers_${context.evaluation_uuid || 'local'}_${bankCacheKey(context)}`;
export const bankOrderCacheKey = (context) => `${STORAGE_KEYS.QUIZ_ORDER_PREFIX}adult14_${context.evaluation_uuid || 'local'}_${bankCacheKey(context)}`;

export const validateAdultBank = (bank, context = {}) => {
  const language = normalizeAssessmentLanguage(context.assessment_language || context.defaultLang);
  if (!bank || bank.assessment_type !== 'adult14' || bank.language !== language ||
      !bank.version || bank.version === 'latest' || !bank.checksum || !bank.scoring_version ||
      bank.question_count !== 140 || !Array.isArray(bank.questions) || bank.questions.length !== 140) {
    throw new Error('Adult question-bank response is incomplete or belongs to another language.');
  }
  for (const [field, actual] of [['question_bank_version', bank.version],
    ['question_bank_checksum', bank.checksum], ['scoring_version', bank.scoring_version]]) {
    if (context[field] && context[field] !== actual) throw new Error('The bound question-bank version has changed.');
  }
  const ids = bank.questions.map(question => question.id);
  if (new Set(ids).size !== 140 || ids.some(id => !Number.isInteger(id) || id < 1 || id > 140) ||
      bank.questions.some(question => typeof question.question !== 'string' || !question.question.trim())) {
    throw new Error('Adult question-bank question IDs or text are invalid.');
  }
  if (!context.question_bank_version && !bank.is_active) throw new Error('This question-bank release is not active.');
  return bank;
};

export const persistAssessmentMetadata = (context, metadata) => {
  const identity = { useruuid: context.useruuid, evaluation_uuid: context.evaluation_uuid };
  const user = safeGet(STORAGE_KEYS.USER_INFO, {});
  const active = safeGet(STORAGE_KEYS.ACTIVE_SESSION, {});
  const sameRecord = value => !value.evaluation_uuid || value.evaluation_uuid === context.evaluation_uuid;
  // Preserve another record's answers under its own immutable bank before switching.
  if (active.evaluation_uuid && active.evaluation_uuid !== context.evaluation_uuid) {
    if (active.question_bank_version && active.question_bank_checksum) {
      safeSet(bankAnswerCacheKey(active), safeGet(STORAGE_KEYS.QUIZ_ANSWERS, {}));
    }
    safeRemove(STORAGE_KEYS.QUIZ_ANSWERS);
    safeRemove(STORAGE_KEYS.UNSYNCED_ANSWERS);
  }
  const next = { ...identity, ...metadata, defaultLang: uiLanguage(metadata.assessment_language || context.assessment_language || context.defaultLang) };
  safeSet(STORAGE_KEYS.USER_INFO, { ...(sameRecord(user) ? user : {}), ...next });
  safeSet(STORAGE_KEYS.ACTIVE_SESSION, { ...(sameRecord(active) ? active : {}), ...next });
  if (context.useruuid) {
    const sessions = safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {});
    const previous = sessions[context.useruuid] || {};
    sessions[context.useruuid] = { ...(sameRecord(previous) ? previous : {}), ...next };
    safeSet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, sessions);
  }
  return { ...context, ...next };
};

export const loadAdultQuestionBank = async (context) => {
  const language = normalizeAssessmentLanguage(context.assessment_language || context.defaultLang);
  const version = context.question_bank_version || 'latest'; // Supported explicitly by QuestionBankService.get_bank.
  let bank;
  let response;
  try {
    response = await axios.get(`${API_BASE_URL}/api/assessment/question-bank`, {
      params: { assessment_type: 'adult14', assessment_language: language, version },
      headers: { Authorization: `Bearer ${API_TOKEN}` }, timeout: 10000,
    });
  } catch (error) {
    // HTTP rejections cannot fall back to a cached release. Offline recovery is exact-version only.
    if (error.response || !context.question_bank_version || !context.question_bank_checksum) throw error;
    bank = safeGet(bankCacheKey(context), null);
  }
  if (response) {
    if (response.data?.status !== 'ok' || response.data?.schema_version !== 1) {
      throw new Error('Unsupported question-bank response schema.');
    }
    bank = response.data.bank;
  }
  bank = validateAdultBank(bank, context);
  const metadata = {
    ...assessmentMetadata(context), assessment_language: bank.language,
    question_bank_version: bank.version, question_bank_checksum: bank.checksum,
    scoring_version: bank.scoring_version,
  };
  safeSet(bankCacheKey(metadata), bank);
  persistAssessmentMetadata(context, metadata);
  return bank;
};

export const restoreAdultAnswers = (context, serverAnswers = [], questionIds = Array.from({ length: 140 }, (_, index) => index + 1)) => {
  const cached = safeGet(bankAnswerCacheKey(context), {});
  const local = safeGet(STORAGE_KEYS.QUIZ_ANSWERS, {});
  const merged = Object.fromEntries(serverAnswers.filter(row => Number.isInteger(Number(row.no)) &&
    ['0', '1', '2'].includes(String(row.fraction))).map(row => [row.no, String(row.fraction)]));
  // The most recent local answer wins over a delayed server checkpoint.
  Object.assign(merged, cached, local);
  const validIds = new Set(questionIds);
  const answers = Object.fromEntries(Object.entries(merged)
    .filter(([id, value]) => validIds.has(Number(id)) && ['0', '1', '2'].includes(String(value)))
    .map(([id, value]) => [Number(id), String(value)]));
  safeSet(STORAGE_KEYS.QUIZ_ANSWERS, answers);
  safeSet(bankAnswerCacheKey(context), answers);
  return answers;
};
